import { useState } from "react";
import { Upload, Loader2, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { tr } from "@/lib/translate";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Universal uploader: drag-and-drop + file picker.
 * accept: e.g. "image/*" or "audio/*"
 * onUploaded: (publicUrl) => void   — receives an absolute URL backed by Object Storage
 */
export default function FileUploader({ accept = "image/*", label = "Choisir un fichier", onUploaded, dataTestid, preview, onClear }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      const url = `${BACKEND}${data.url}`;
      onUploaded(url);
    } catch (err) {
      toast.error(tr(err.response?.data?.detail || "Could not publish"));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  return (
    <div className="relative">
      {preview ? (
        <div className="relative rounded-xl overflow-hidden border border-white/10 bg-zinc-900">
          {accept.startsWith("audio") ? (
            <audio controls src={preview} className="w-full" />
          ) : (
            <img src={preview} alt="" className="w-full max-h-64 object-cover" />
          )}
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 text-white hover:bg-black"
              data-testid={`${dataTestid}-clear`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <label
          className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition ${busy ? "border-[#FF0050] bg-[#FF0050]/5" : "border-zinc-700 hover:border-[#00F2FE] bg-zinc-900/40"}`}
          data-testid={dataTestid}
        >
          <input
            type="file"
            accept={accept}
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            disabled={busy}
            data-testid={`${dataTestid}-input`}
          />
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin text-[#FF0050]" />
              <span className="text-sm text-white">{progress}%</span>
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-zinc-300">{label}</span>
            </>
          )}
        </label>
      )}
    </div>
  );
}
